import React, { useState, useEffect } from 'react';
import { motion, useMotionValue, useSpring, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../store/languageStore';
import { authApi } from '../api/auth.api';
import { useAuthStore } from '../store/authStore';
import { Input } from '../components/common/Input';
import { Button } from '../components/common/Button';
import { LanguageSwitcher } from '../components/common/LanguageSwitcher';
import { User, Lock, Shield, Zap, Activity, Star, HeartPulse, AlertCircle, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { extractLocalizedMessage } from '../utils/helpers';

interface LoginFormData {
  username: string;
  password: string;
}

export const LoginPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const { language, isRTL } = useLanguageStore();
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>();

  // Mouse tracking for parallax effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  React.useEffect(() => {
    i18n.changeLanguage(language);
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRTL, i18n]);

  const onSubmit = async (data: LoginFormData) => {
    setGeneralError(null);
    setIsLoading(true);

    try {
      const response = await authApi.login(data);
      login(response.token, response.user);
      toast.success(t('common.welcome'));
      navigate('/');
    } catch (err: unknown) {
      let errorMessage = t('errors.loginFailed');
      let hasFieldErrors = false;

      // Network error: backend unreachable (no response from server)
      const isNetworkError =
        err instanceof Error &&
        (err as { response?: unknown }).response === undefined &&
        (err.message.includes('Network error') ||
          err.message.includes('Server unreachable') ||
          err.message.includes('ECONNREFUSED'));
      if (isNetworkError) {
        errorMessage = t('errors.serverUnreachable');
        setGeneralError(errorMessage);
        toast.error(errorMessage);
        setIsLoading(false);
        return;
      }

      if (err instanceof Error) {
        const fieldErrors = (err as any).fieldErrors;
        if (fieldErrors) {
          if (fieldErrors.username) {
            const usernameError = Array.isArray(fieldErrors.username) 
              ? fieldErrors.username[0] 
              : fieldErrors.username;
            const localizedError = extractLocalizedMessage(usernameError, language);
            setError('username', { type: 'server', message: localizedError });
            hasFieldErrors = true;
          }
          if (fieldErrors.password) {
            const passwordError = Array.isArray(fieldErrors.password) 
              ? fieldErrors.password[0] 
              : fieldErrors.password;
            const localizedError = extractLocalizedMessage(passwordError, language);
            setError('password', { type: 'server', message: localizedError });
            hasFieldErrors = true;
          }
        } else {
          if (err.message.includes('username:') || err.message.includes('Username:')) {
            const usernameError = err.message.match(/[Uu]sername[:\s]+([^\n]+)/)?.[1] || 
                                 err.message.match(/username[:\s]+([^\n]+)/)?.[1];
            if (usernameError) {
              const localizedError = extractLocalizedMessage(usernameError, language);
              setError('username', { type: 'server', message: localizedError });
              hasFieldErrors = true;
            }
          }
          
          if (err.message.includes('password:') || err.message.includes('Password:')) {
            const passwordError = err.message.match(/[Pp]assword[:\s]+([^\n]+)/)?.[1] || 
                                 err.message.match(/password[:\s]+([^\n]+)/)?.[1];
            if (passwordError) {
              const localizedError = extractLocalizedMessage(passwordError, language);
              setError('password', { type: 'server', message: localizedError });
              hasFieldErrors = true;
            }
          }
        }
        
        if (!hasFieldErrors) {
          const localizedMessage = extractLocalizedMessage(err.message, language);
          
          if (localizedMessage.includes('\n')) {
            const errors = localizedMessage.split('\n');
            errorMessage = errors[0];
          } else {
            errorMessage = localizedMessage;
          }
          
          if (errorMessage.includes('اسم المستخدم أو كلمة المرور غير صحيحة') || 
              errorMessage.includes('Invalid username or password')) {
            errorMessage = t('errors.invalidCredentials');
          } else if (errorMessage.includes('يرجى إدخال') || 
                     errorMessage.includes('Please enter')) {
            errorMessage = t('errors.emptyCredentials');
          } else if (errorMessage.includes('غير نشط') || 
                     errorMessage.includes('not active')) {
            errorMessage = t('errors.accountInactive');
          }
          
          setGeneralError(errorMessage);
          toast.error(errorMessage);
        } else {
          toast.error(t('errors.validationFailed'));
        }
      } else {
        setGeneralError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Parallax values
  const x = useSpring(useMotionValue(0), { stiffness: 50, damping: 20 });
  const y = useSpring(useMotionValue(0), { stiffness: 50, damping: 20 });

  useEffect(() => {
    x.set(mousePosition.x * 10);
    y.set(mousePosition.y * 10);
  }, [mousePosition, x, y]);

  return (
    <div 
      className="min-vh-100 d-flex align-items-center justify-content-center position-relative overflow-hidden login-page-container"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Enhanced CSS Styles Matching Design */}
      <style>{`
        .login-page-container {
          background: linear-gradient(135deg, #0f0c29 0%, #302b63 25%, #24243e 50%, #0f0c29 75%, #302b63 100%);
          background-size: 400% 400%;
          animation: gradientShift 20s ease infinite;
          font-family: 'Cairo', 'Segoe UI', 'Roboto', sans-serif;
        }
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .particle {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
        }
        .login-card-glow {
          box-shadow: 0 0 60px rgba(102, 126, 234, 0.4), 0 0 100px rgba(118, 75, 162, 0.2);
        }
        .gradient-text {
          background: linear-gradient(135deg, #ffffff 0%, #e0e7ff 50%, #c7d2fe 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .login-title {
          font-family: 'Cairo', 'Segoe UI', sans-serif;
          font-weight: 700;
          letter-spacing: ${language === 'ar' ? '0' : '0.02em'};
        }
        .login-subtitle {
          font-family: 'Cairo', 'Segoe UI', sans-serif;
          font-weight: 500;
          letter-spacing: ${language === 'ar' ? '0' : '0.3px'};
        }
        .login-input {
          font-family: 'Cairo', 'Segoe UI', sans-serif;
        }
        .login-button {
          font-family: 'Cairo', 'Segoe UI', sans-serif;
          font-weight: 600;
          letter-spacing: ${language === 'ar' ? '0' : '0.5px'};
        }
      `}</style>

      {/* Language Switcher - Fixed Position */}
      <motion.div 
        className="position-fixed"
        style={{
          top: '2rem',
          [isRTL ? 'left' : 'right']: '2rem',
          zIndex: 1000,
        }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3 }}
      >
        <LanguageSwitcher />
      </motion.div>

      {/* Animated Background Particles */}
      <div className="position-absolute w-100 h-100" style={{ overflow: 'hidden', zIndex: 1 }}>
        {[...Array(60)].map((_, i) => (
          <motion.div
            key={i}
            className="particle"
            style={{
              width: Math.random() * 5 + 2,
              height: Math.random() * 5 + 2,
              background: `rgba(255, 255, 255, ${Math.random() * 0.6 + 0.2})`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              boxShadow: `0 0 ${Math.random() * 10 + 5}px rgba(255, 255, 255, 0.5)`,
            }}
            animate={{
              y: [0, -150, 0],
              opacity: [0, 1, 0],
              scale: [0, 1.5, 0],
            }}
            transition={{
              duration: Math.random() * 4 + 3,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
          />
        ))}

        {/* Large Floating Orbs */}
        <motion.div
          className="position-absolute rounded-circle"
          style={{
            width: '700px',
            height: '700px',
            background: 'radial-gradient(circle, rgba(102, 126, 234, 0.4) 0%, rgba(118, 75, 162, 0.2) 40%, transparent 70%)',
            top: '-250px',
            left: '-250px',
            filter: 'blur(80px)',
          }}
          animate={{
            x: [0, 120, 0],
            y: [0, 120, 0],
            scale: [1, 1.3, 1],
            opacity: [0.4, 0.6, 0.4],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            repeatType: 'reverse',
          }}
        />
        <motion.div
          className="position-absolute rounded-circle"
          style={{
            width: '600px',
            height: '600px',
            background: 'radial-gradient(circle, rgba(79, 172, 254, 0.35) 0%, rgba(0, 242, 254, 0.15) 40%, transparent 70%)',
            bottom: '-200px',
            right: '-200px',
            filter: 'blur(70px)',
          }}
          animate={{
            x: [0, -100, 0],
            y: [0, -80, 0],
            scale: [1, 1.4, 1],
            opacity: [0.4, 0.7, 0.4],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            repeatType: 'reverse',
          }}
        />
        <motion.div
          className="position-absolute rounded-circle"
          style={{
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(240, 147, 251, 0.3) 0%, rgba(102, 126, 234, 0.15) 40%, transparent 70%)',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            filter: 'blur(60px)',
          }}
          animate={{
            rotate: [0, 360],
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 35,
            repeat: Infinity,
            repeatType: 'reverse',
          }}
        />

        {/* Animated Grid */}
        <div
          className="position-absolute w-100 h-100"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px',
            opacity: 0.7,
          }}
        />
      </div>

      {/* Main Login Card */}
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.8, type: 'spring', stiffness: 100, damping: 20 }}
        className="position-relative"
        style={{ 
          zIndex: 10, 
          width: '100%', 
          maxWidth: '500px', 
          padding: '0 1.5rem',
          transform: `translate(${x}px, ${y}px)`,
        }}
      >
        {/* Glow Effect Behind Card */}
        <motion.div
          className="position-absolute rounded-5 login-card-glow"
          style={{
            width: '110%',
            height: '110%',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.5), rgba(118, 75, 162, 0.5))',
            filter: 'blur(50px)',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: -1,
          }}
          animate={{
            opacity: [0.4, 0.7, 0.4],
            scale: [1, 1.15, 1],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
          }}
        />

        {/* Main Card */}
        <div
          className="rounded-5 shadow-lg border-0 p-5 position-relative overflow-hidden"
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(40px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 30px 100px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.15) inset, 0 0 200px rgba(102, 126, 234, 0.2)',
          }}
        >
          {/* Shimmer Effect */}
          <motion.div
            className="position-absolute w-100 h-100"
            style={{
              top: 0,
              left: '-100%',
              background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent)',
              zIndex: 1,
            }}
            animate={{
              left: ['-100%', '200%'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              repeatDelay: 2,
            }}
          />

          {/* Logo/Header Section - Matching Design */}
          <motion.div
            className="text-center mb-5 position-relative"
            style={{ zIndex: 2 }}
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            {/* Main Logo Icon with Two Rings */}
            <div className="position-relative d-inline-block mb-4">
              <motion.div
                className="d-inline-flex align-items-center justify-content-center rounded-circle position-relative"
                style={{
                  width: '110px',
                  height: '110px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                  boxShadow: '0 20px 50px rgba(102, 126, 234, 0.6), 0 0 0 5px rgba(255, 255, 255, 0.15)',
                }}
                animate={{
                  rotate: [0, 360],
                  boxShadow: [
                    '0 20px 50px rgba(102, 126, 234, 0.6), 0 0 0 5px rgba(255, 255, 255, 0.15)',
                    '0 25px 70px rgba(102, 126, 234, 0.8), 0 0 0 8px rgba(255, 255, 255, 0.25)',
                    '0 20px 50px rgba(102, 126, 234, 0.6), 0 0 0 5px rgba(255, 255, 255, 0.15)',
                  ],
                }}
                transition={{
                  rotate: { duration: 25, repeat: Infinity, ease: 'linear' },
                  boxShadow: { duration: 2.5, repeat: Infinity },
                }}
              >
                <Activity className="h-14 w-14 text-white" strokeWidth={2.5} />
              </motion.div>
              
              {/* Outer Rotating Ring - Dashed */}
              <motion.div
                className="position-absolute rounded-circle border"
                style={{
                  width: '140px',
                  height: '140px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  top: '-15px',
                  left: '-15px',
                  borderStyle: 'dashed',
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
              />
              
              {/* Inner Rotating Ring - Solid */}
              <motion.div
                className="position-absolute rounded-circle border"
                style={{
                  width: '125px',
                  height: '125px',
                  border: '2px solid rgba(255, 255, 255, 0.25)',
                  top: '-7.5px',
                  left: '-7.5px',
                }}
                animate={{ rotate: -360 }}
                transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
              />
            </div>

            {/* Floating Icons - Matching Design Colors */}
            {/* Green Icon - Top Left */}
            <motion.div
              className="position-absolute d-flex align-items-center justify-content-center rounded-circle"
              style={{
                top: '30px',
                [isRTL ? 'right' : 'left']: '30px',
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 6px 20px rgba(16, 185, 129, 0.5)',
                zIndex: 3,
              }}
              animate={{
                y: [0, -15, 0],
                scale: [1, 1.15, 1],
                rotate: [0, 180, 360],
              }}
              transition={{
                duration: 3.5,
                repeat: Infinity,
              }}
            >
              <Star className="h-5 w-5 text-white" strokeWidth={2.5} fill="currentColor" />
            </motion.div>

            {/* Purple Icon - Top Right */}
            <motion.div
              className="position-absolute d-flex align-items-center justify-content-center rounded-circle"
              style={{
                top: '30px',
                [isRTL ? 'left' : 'right']: '30px',
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                boxShadow: '0 6px 20px rgba(139, 92, 246, 0.5)',
                zIndex: 3,
              }}
              animate={{
                y: [0, -12, 0],
                scale: [1, 1.2, 1],
                rotate: [360, 180, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
              }}
            >
              <Star className="h-5 w-5 text-white" strokeWidth={2.5} fill="currentColor" />
            </motion.div>

            {/* Orange Icon - Mid Right */}
            <motion.div
              className="position-absolute d-flex align-items-center justify-content-center rounded-circle"
              style={{
                top: '50%',
                [isRTL ? 'left' : 'right']: '-20px',
                transform: 'translateY(-50%)',
                width: '36px',
                height: '36px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                boxShadow: '0 6px 20px rgba(245, 158, 11, 0.5)',
                zIndex: 3,
              }}
              animate={{
                x: [0, 10, 0],
                scale: [1, 1.1, 1],
                rotate: [0, -180, -360],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
              }}
            >
              <Zap className="h-4.5 w-4.5 text-white" strokeWidth={2.5} />
            </motion.div>

            {/* Title - Matching Design */}
            <motion.h1
              className="login-title mb-3 position-relative gradient-text"
              style={{
                fontSize: language === 'ar' ? '2.5rem' : '2.75rem',
                letterSpacing: language === 'ar' ? '0' : '0.02em',
                textShadow: '0 0 40px rgba(102, 126, 234, 0.6)',
                color: '#ffffff',
                lineHeight: '1.2',
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, type: 'spring', stiffness: 200 }}
            >
              {t('login.title')}
            </motion.h1>
            <motion.p
              className="login-subtitle mb-0"
              style={{ 
                fontSize: '1.1rem', 
                letterSpacing: language === 'ar' ? '0' : '0.3px',
                color: 'rgba(255, 255, 255, 0.7)',
                lineHeight: '1.5',
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {t('login.subtitle')}
            </motion.p>
          </motion.div>

          {/* Professional Error Message */}
          <AnimatePresence>
            {generalError && (
              <motion.div
                initial={{ opacity: 0, y: -20, scale: 0.95, height: 0 }}
                animate={{ opacity: 1, y: 0, scale: 1, height: 'auto' }}
                exit={{ opacity: 0, y: -10, scale: 0.95, height: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                className="mb-4 position-relative"
                style={{ zIndex: 2 }}
              >
                <div
                  className="rounded-xl position-relative overflow-hidden border-0"
                  style={{
                    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.1) 100%)',
                    backdropFilter: 'blur(20px) saturate(180%)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    boxShadow: '0 8px 32px rgba(239, 68, 68, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
                  }}
                >
                  {/* Animated Shimmer Effect */}
                  <motion.div
                    className="position-absolute w-100 h-100"
                    style={{
                      top: 0,
                      left: '-100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                      zIndex: 1,
                    }}
                    animate={{
                      left: ['-100%', '200%'],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatDelay: 1,
                    }}
                  />

                  {/* Glow Effect */}
                  <motion.div
                    className="position-absolute rounded-circle"
                    style={{
                      width: '200px',
                      height: '200px',
                      background: 'radial-gradient(circle, rgba(239, 68, 68, 0.3) 0%, transparent 70%)',
                      top: '-100px',
                      [isRTL ? 'right' : 'left']: '-100px',
                      filter: 'blur(40px)',
                      zIndex: 0,
                    }}
                    animate={{
                      opacity: [0.3, 0.5, 0.3],
                      scale: [1, 1.2, 1],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                    }}
                  />

                  {/* Content */}
                  <div className="position-relative d-flex align-items-start gap-3 p-4" style={{ zIndex: 2 }}>
                    {/* Icon Container */}
                    <motion.div
                      className="flex-shrink-0"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    >
                      <div className="position-relative">
                        <motion.div
                          className="position-absolute rounded-circle"
                          style={{
                            width: '100%',
                            height: '100%',
                            background: 'rgba(239, 68, 68, 0.3)',
                            filter: 'blur(12px)',
                            top: 0,
                            left: 0,
                          }}
                          animate={{
                            scale: [1, 1.3, 1],
                            opacity: [0.5, 0.8, 0.5],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                          }}
                        />
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle position-relative"
                          style={{
                            width: '44px',
                            height: '44px',
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            boxShadow: '0 4px 16px rgba(239, 68, 68, 0.4)',
                          }}
                        >
                          <motion.div
                            animate={{ rotate: [0, -10, 10, 0] }}
                            transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 2 }}
                          >
                            <AlertCircle className="h-5 w-5 text-white" strokeWidth={2.5} />
                          </motion.div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Text Content */}
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <motion.div
                        initial={{ opacity: 0, x: isRTL ? 10 : -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="d-flex align-items-center gap-2 mb-2"
                      >
                        <h6
                          className="mb-0 fw-bold"
                          style={{
                            fontSize: '0.875rem',
                            color: '#dc2626',
                            letterSpacing: '0.02em',
                            fontFamily: language === 'ar' ? "'Cairo', 'Noto Sans Arabic', sans-serif" : "'Inter', 'Segoe UI', sans-serif",
                          }}
                        >
                          {t('common.error', 'Error')}
                        </h6>
                        <motion.span
                          animate={{ opacity: [1, 0.3, 1] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                          style={{
                            fontSize: '0.75rem',
                            color: '#ef4444',
                          }}
                        >
                          ●
                        </motion.span>
                      </motion.div>
                      <motion.p
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="mb-0"
                        style={{
                          fontSize: '0.9375rem',
                          color: 'rgba(255, 255, 255, 0.95)',
                          lineHeight: '1.6',
                          fontFamily: language === 'ar' ? "'Cairo', 'Noto Sans Arabic', sans-serif" : "'Inter', 'Segoe UI', sans-serif",
                          fontWeight: 500,
                        }}
                      >
                        {generalError}
                      </motion.p>
                    </div>

                    {/* Close Button */}
                    <motion.button
                      type="button"
                      onClick={() => setGeneralError(null)}
                      className="btn btn-link border-0 p-0 bg-transparent flex-shrink-0 d-flex align-items-center justify-content-center"
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        color: 'rgba(255, 255, 255, 0.7)',
                        transition: 'all 0.2s ease-in-out',
                      }}
                      whileHover={{
                        scale: 1.1,
                        color: 'rgba(255, 255, 255, 1)',
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      }}
                      whileTap={{ scale: 0.9 }}
                      title={t('common.close', 'Close')}
                    >
                      <X className="h-4 w-4" strokeWidth={2.5} />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="position-relative" style={{ zIndex: 2 }}>
            {/* Username Field */}
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="mb-4 position-relative"
            >
              <Input
                label={t('login.username')}
                required
                error={errors.username?.message}
                icon={<User className="h-5 w-5" strokeWidth={2} />}
                placeholder={t('login.enterUsername')}
                className="login-input"
                {...register('username', { required: t('errors.usernameRequired') })}
              />
            </motion.div>

            {/* Password Field - Custom Input Group */}
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="mb-4"
            >
              <label 
                className={`form-label fw-semibold mb-2 d-block ${isRTL ? 'text-end' : 'text-start'}`} 
                style={{ 
                  fontSize: '0.875rem', 
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontFamily: language === 'ar' ? "'Cairo', 'Noto Sans Arabic', sans-serif" : "'Inter', 'Segoe UI', sans-serif",
                  letterSpacing: '0.3px',
                }}
              >
                {t('login.password')}
                <span className="text-danger ms-1">*</span>
              </label>
              
              <motion.div
                className="input-group position-relative"
                whileFocus={{ scale: 1.01 }}
                style={{
                  borderRadius: '0.75rem',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
              >
                {/* Lock Icon - Left Side */}
                {!isRTL && (
                  <span 
                    className="input-group-text bg-white border-0 d-flex align-items-center justify-content-center" 
                    style={{ 
                      width: '48px',
                      minWidth: '48px',
                      padding: '0.75rem',
                      borderTopLeftRadius: '0.75rem',
                      borderBottomLeftRadius: '0.75rem',
                      borderRight: 'none',
                    }}
                  >
                    <Lock className="h-5 w-5" strokeWidth={2} style={{ color: '#1f2937' }} />
                  </span>
                )}
                
                {/* Password Input */}
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`form-control ${errors.password ? 'is-invalid' : ''} login-input`}
                  placeholder={t('login.enterPassword')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{
                    backgroundColor: '#ffffff',
                    border: 'none',
                    borderRadius: isRTL ? '0.75rem 0 0 0.75rem' : '0 0.75rem 0.75rem 0',
                    padding: '0.75rem 1rem',
                    fontSize: '1rem',
                    color: '#1f2937',
                    fontFamily: language === 'ar' ? "'Cairo', 'Noto Sans Arabic', sans-serif" : "'Inter', 'Segoe UI', sans-serif",
                    boxShadow: 'none',
                    flex: 1,
                    ...(errors.password && { 
                      boxShadow: '0 0 0 2px rgba(239, 68, 68, 0.2)',
                    }),
                  }}
                  {...register('password', { required: t('errors.passwordRequired') })}
                />
                
                {/* Lock Icon - Right Side (RTL) */}
                {isRTL && (
                  <span 
                    className="input-group-text bg-white border-0 d-flex align-items-center justify-content-center" 
                    style={{ 
                      width: '48px',
                      minWidth: '48px',
                      padding: '0.75rem',
                      borderTopRightRadius: '0.75rem',
                      borderBottomRightRadius: '0.75rem',
                      borderLeft: 'none',
                    }}
                  >
                    <Lock className="h-5 w-5" strokeWidth={2} style={{ color: '#1f2937' }} />
                  </span>
                )}
              </motion.div>
              
              {/* Error Message */}
              {errors.password && (
                <motion.div
                  initial={{ opacity: 0, y: -5, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -5, height: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  className={`mt-2 ${isRTL ? 'text-end' : 'text-start'}`}
                >
                  <p className="mb-0 small fw-medium" style={{ color: '#ef4444', fontSize: '0.875rem' }}>
                    {errors.password.message}
                  </p>
                </motion.div>
              )}
            </motion.div>

            {/* Submit Button - Matching Design */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="mt-4"
            >
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="position-relative"
              >
                <Button
                  type="submit"
                  isLoading={isLoading}
                  variant="gradient"
                  fullWidth
                  className="position-relative overflow-hidden border-0 login-button"
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    padding: '1.125rem 2rem',
                    fontSize: language === 'ar' ? '1.1rem' : '1.125rem',
                    fontWeight: 600,
                    boxShadow: '0 10px 30px rgba(59, 130, 246, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
                    color: 'white',
                    letterSpacing: language === 'ar' ? '0' : '0.5px',
                    borderRadius: '0.75rem',
                  }}
                >
                  <motion.span
                    className="position-relative d-flex align-items-center justify-content-center gap-2"
                    style={{ zIndex: 2 }}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        {t('login.signingIn')}
                      </>
                    ) : (
                      <>
                        <HeartPulse className="h-5 w-5" strokeWidth={2.5} />
                        {t('login.signIn')}
                      </>
                    )}
                  </motion.span>
                  <motion.div
                    className="position-absolute w-100 h-100"
                    style={{
                      top: 0,
                      left: '-100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.35), transparent)',
                    }}
                    animate={{
                      left: ['-100%', '200%'],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      repeatDelay: 1,
                    }}
                  />
                </Button>
              </motion.div>
            </motion.div>
          </form>

          {/* Footer - Matching Design */}
          <motion.div
            className="text-center mt-5 pt-4 position-relative"
            style={{ 
              borderTop: '1px solid rgba(255, 255, 255, 0.15)',
              zIndex: 2,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <motion.p
              className="mb-0 d-flex align-items-center justify-content-center gap-2 fw-medium"
              style={{ 
                fontSize: '0.95rem',
                letterSpacing: language === 'ar' ? '0' : '0.3px',
                color: 'rgba(255, 255, 255, 0.6)',
                fontFamily: language === 'ar' ? "'Cairo', 'Noto Sans Arabic', sans-serif" : "'Inter', 'Segoe UI', sans-serif",
              }}
              whileHover={{ scale: 1.05 }}
            >
              <Shield className="h-4.5 w-4.5" strokeWidth={2} />
              <span>{t('login.secureAccess')}</span>
            </motion.p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};
